import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { auftraegeUrl, auftragVorlageUrl, type AuftragVorlage } from './AuftragDaten';
import {
  customerBody,
  drittlandArt,
  emptyRow,
  free,
  kundeWerteOf,
  LEERER_KUNDE,
  plusTage,
  positionBody,
  ratesFor,
  rowOf,
  summen,
  type KundeWerte,
  type PosRow,
} from './formular';
import { KundeWahl } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import { PdfDialog } from './PdfDialog';
import { Positionen, SteuerHinweisFeld, Summen, useSteuerHinweis } from './Positionen';
import { darf, fehlerText } from './tarifHinweis';
import type { Kunde, Overview, Rate } from './types';
import { urlId } from './urlId';
import { useWaehrung, WaehrungFeld } from './WaehrungFeld';
import { waehrungAus } from './waehrung';
import './formular.css';

// wie auf dem Server (auftraege.ts): Liefertermin ab heute, höchstens zwei Jahre
const LIEFERTERMIN_MAX = 730;

// Neue Auftragsbestätigung: Kunde (aus der Kundenliste oder neu), Einsatzort, Liefertermin, Bestellnummer des Kunden,
// Einleitung, Positionen und Hinweis. Vorschau als PDF, dann schreiben (eigene Nummer, danach fest). Keine Entwürfe:
// das Formular hält nur, was gerade da steht.
// Aufruf mit ?angebot=<id> (aus einem Angebot, „Erst bearbeiten“ in Angebote.tsx) oder ?vorlage=<id> („Kopieren“).
// Währung und Kurs: aus dem Angebot (die Währung dann fest), sonst die des Kunden mit dem Kurs von heute.
export function AuftragForm({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const ku = data.absender.kleinunternehmer;
  const leistungen = useLeistungen(data.firm.id);
  const [params] = useSearchParams();
  // IDs aus der Adresse nur als UUID (urlId.ts)
  const angebotParam = urlId(params.get('angebot'));
  const vorlageId = angebotParam ? null : urlId(params.get('vorlage'));
  // die AB wird mit diesem Angebot verbunden (Server: angebotId)
  const [angebotId, setAngebotId] = useState<string | null>(null);
  const [kunde, setKundeState] = useState<KundeWerte>(LEERER_KUNDE);
  const [rows, setRows] = useState<PosRow[]>(() => [emptyRow()]);
  const drittland = drittlandArt(kunde);
  const steuerHinweis = useSteuerHinweis(rows, ku, data.hinweis0, drittland);
  const wae = useWaehrung({ firmId: data.firm.id, tarif: data.tarif, datum: data.today, preiseDa: rows.some((r) => r.price.trim() !== '') });
  const [site, setSite] = useState('');
  const [liefertermin, setLiefertermin] = useState('');
  const [bestellnummer, setBestellnummer] = useState('');
  const [intro, setIntro] = useState('');
  const [note, setNote] = useState('');
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laden, setLaden] = useState(!!(angebotParam || vorlageId));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  // Übersicht zum Zeitpunkt des Ladens (der Effekt unten läuft nur einmal je Adresse)
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const rates: Rate[] = ratesFor(ku);
  const total = summen(rows, ku).gross;
  const satz: Rate = drittland === 'firma' && !ku ? 0 : 19;
  // Kunde außerhalb der EU (Firma) gewählt oder nicht mehr: leere Positionen mit dem passenden Satz
  useEffect(() => {
    setRows((cur) => (cur.some((r) => free(r) && r.rate !== satz) ? cur.map((r) => (free(r) ? { ...r, rate: satz } : r)) : cur));
  }, [satz]);
  // Kunde aus der Liste gewählt: seine Währung (nicht aus dem Angebot, dort gilt dessen Währung)
  const onPick = (k: Kunde | null) => !angebotId && wae.setW(waehrungAus(k?.waehrung));

  // Aus dem Angebot (dieselben Prüfungen wie beim Schreiben) oder „Kopieren“ einer AB: Kunde, Einsatzort, Positionen
  // und Texte; Datum neu, Liefertermin leer
  useEffect(() => {
    const von = angebotParam ? ('angebot' as const) : vorlageId ? ('auftrag' as const) : null;
    const id = angebotParam ?? vorlageId;
    if (!von || !id) return;
    let live = true;
    api
      .get<AuftragVorlage>(auftragVorlageUrl(dataRef.current.firm.id, von, id))
      .then(({ angebot, kopie, form, warnung }) => {
        if (!live) return;
        const kundeId = form.kundeId && dataRef.current.kunden.some((k) => k.id === form.kundeId) ? form.kundeId : null;
        setAngebotId(form.angebotId);
        setKundeState(kundeWerteOf(form.customer, kundeId));
        // Währung und Kurs des Angebots; Kopie: Währung der AB, Kurs von heute
        wae.laden(waehrungAus(form.waehrung), form.kurs != null ? String(form.kurs) : null);
        setRows(form.items.length ? form.items.map((i) => rowOf(i)) : [emptyRow()]);
        steuerHinweis.laden(form.items, form.steuerHinweis);
        setSite(form.site ?? '');
        setIntro(form.intro ?? '');
        setNote(form.note ?? '');
        setBestellnummer(form.bestellnummer ?? '');
        const text = angebot
          ? `Aus Angebot ${angebot.number} vom ${fmtDate(angebot.date)}.${angebot.anzeige === 'abgelaufen' ? ` Das Angebot galt bis ${fmtDate(angebot.validUntil)}.` : ''}`
          : `Kopie von Auftragsbestätigung ${kopie?.number ?? ''}. Bitte Leistungen, Preise und Liefertermin prüfen.`;
        setHinweis(`${text}${warnung ? ` ${warnung}` : ''}`);
      })
      .catch((err) => live && setError(errorText(err)))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angebotParam, vorlageId]);

  function body() {
    return {
      customer: customerBody(kunde, { xrechnung: false }),
      kundeId: kunde.kundeId,
      angebotId,
      items: rows.map((r) => positionBody(r, ku)),
      site,
      intro,
      note,
      bestellnummer,
      steuerHinweis: steuerHinweis.body,
      liefertermin: liefertermin || null,
      ...wae.body,
    };
  }

  async function showPreview() {
    setError(null);
    setBusy(true);
    try {
      const blob = await api.blob(`${auftraegeUrl(data.firm.id)}/vorschau`, body());
      setPreview(URL.createObjectURL(blob));
    } catch (err) {
      if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function write() {
    setBusy(true);
    try {
      const res = await api.post<{ id: string; number: string }>(auftraegeUrl(data.firm.id), body());
      // ein neuer Kunde steht jetzt in der Kundenliste
      await reload();
      toast(`Auftragsbestätigung ${res.number} geschrieben`);
      // gleich die PDF, mit „Per E-Mail“ und „Herunterladen“ (Auftraege.tsx)
      navigate('/rechnung/auftraege', { state: { show: res.id, art: 'auftrag', number: res.number } });
    } catch (err) {
      setConfirm(false);
      if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setConfirm(true);
  }

  return (
    <form className="rt-card rf" onSubmit={submit} noValidate>
      <div className="rt-form-head">
        <h1>Neue Auftragsbestätigung</h1>
        <Link to={angebotParam ? '/rechnung/angebote' : '/rechnung/auftraege'} className="btn small">
          Abbrechen
        </Link>
      </div>
      {hinweis && <p className="rt-vorlage">{hinweis}</p>}

      <h2>Kunde</h2>
      <KundeWahl kunden={data.kunden} items={data.items} today={data.today} value={kunde} onChange={setKundeState} onPick={onPick} />
      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Einsatzort</span>
          <input value={site} maxLength={200} placeholder="z. B. Bürohaus Kehrwieder 7" onChange={(e) => setSite(e.target.value)} />
        </label>
        <label className="field rt-span2">
          <span>Liefertermin (optional)</span>
          <input
            type="date"
            value={liefertermin}
            min={data.today}
            max={plusTage(data.today, LIEFERTERMIN_MAX)}
            onChange={(e) => setLiefertermin(e.target.value)}
          />
        </label>
        <label className="field rt-span2">
          <span>Bestellnummer des Kunden (optional)</span>
          <input value={bestellnummer} maxLength={100} autoComplete="off" onChange={(e) => setBestellnummer(e.target.value)} />
        </label>
        <label className="field rt-span4">
          <span>Einleitung</span>
          <textarea
            value={intro}
            rows={2}
            maxLength={1000}
            placeholder="Vielen Dank für Ihren Auftrag. Wir bestätigen Ihnen folgende Leistungen:"
            onChange={(e) => setIntro(e.target.value)}
          />
        </label>
      </div>

      <h2>Leistungen</h2>
      <Positionen
        rows={rows}
        onChange={setRows}
        ku={ku}
        rates={rates}
        leistungen={leistungen}
        leistungSpeichern={darf(data.tarif, 'kunden')}
        w={wae.w}
        kurs={wae.kurs}
        satz={satz}
      />
      <SteuerHinweisFeld h={steuerHinweis} auf="der Auftragsbestätigung" />
      <Summen rows={rows} ku={ku} titel="Auftragssumme" w={wae.w} kurs={wae.kurs} />
      <WaehrungFeld h={wae} fest={!!angebotId} />

      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Hinweis</span>
          <textarea
            value={note}
            rows={2}
            maxLength={1000}
            placeholder="Wir freuen uns auf die Zusammenarbeit."
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="notice bad">{error}</div>}
      <div className="rt-actions rf-actions">
        <button type="button" className="btn" disabled={busy || laden} onClick={() => void showPreview()}>
          Vorschau
        </button>
        <button type="submit" className="btn primary" disabled={busy || laden}>
          Auftragsbestätigung schreiben
        </button>
      </div>

      <PdfDialog url={preview} title="Vorschau" onClose={() => setPreview(null)} />
      <ConfirmDialog
        open={confirm}
        title="Auftragsbestätigung jetzt schreiben?"
        text={`Sie bekommt die nächste Nummer und das Datum von heute. Danach lässt sie sich nicht mehr ändern, nur kopieren. Auftragssumme: ${eurC(total / 100, wae.w)}.`}
        confirmLabel="Ja, schreiben"
        busy={busy}
        onConfirm={() => void write()}
        onCancel={() => setConfirm(false)}
      />
    </form>
  );
}
